/**
 * FeeCalculatorPreview Component
 *
 * Interactive fee calculator showing breakdown for sample bookings.
 */

import { useState } from 'react';
import type { PlatformConfig } from '../hooks';
import { calculateFees } from '../hooks';

interface FeeCalculatorPreviewProps {
  config: PlatformConfig;
}

export const FeeCalculatorPreview = ({ config }: FeeCalculatorPreviewProps) => {
  const [bookingAmount, setBookingAmount] = useState(200);

  const fees = calculateFees(bookingAmount, config);

  const presets = [50, 100, 200, 500, 1000];

  return (
    <div className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Sample Booking Amount ($)
        </label>
        <div className="flex gap-2 mb-3">
          {presets.map((preset) => (
            <button
              key={preset}
              onClick={() => setBookingAmount(preset)}
              className={`px-3 py-1 text-sm rounded-lg border transition-colors ${
                bookingAmount === preset
                  ? 'bg-primary-500 text-white border-primary-500'
                  : 'bg-white text-gray-700 border-gray-300 hover:border-primary-500'
              }`}
            >
              ${preset}
            </button>
          ))}
        </div>
        <input
          type="number"
          value={bookingAmount}
          onChange={(e) => setBookingAmount(Math.max(0, parseFloat(e.target.value) || 0))}
          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          min="0"
          step="10"
        />
      </div>

      <div className="bg-gray-50 rounded-xl p-5 space-y-3">
        <h4 className="font-semibold text-gray-900 mb-4">Fee Breakdown</h4>

        <div className="space-y-2">
          {/* Base Amount */}
          <div className="flex justify-between text-sm">
            <span className="text-gray-600">Booking Amount</span>
            <span className="font-medium">${bookingAmount.toFixed(2)}</span>
          </div>

          {/* Guest Total - no extra fee */}
          <div className="flex justify-between pt-2 border-t border-gray-200">
            <span className="font-semibold text-gray-900">Guest Pays</span>
            <span className="font-bold text-lg">${fees.guestTotal.toFixed(2)}</span>
          </div>
        </div>

        <div className="border-t border-gray-200 pt-3 mt-3 space-y-2">
          {/* Platform Commission */}
          <div className="flex justify-between text-sm">
            <span className="text-gray-600">
              - Platform Commission (10%)
              <span className="text-gray-400 ml-1">
                ({config.fees.platformCommission.value}%)
              </span>
            </span>
            <span className="font-medium text-red-600">-${fees.platformCommission.toFixed(2)}</span>
          </div>

          {/* Host Payout */}
          <div className="flex justify-between pt-2 border-t border-gray-200">
            <span className="font-semibold text-gray-900">Host Receives</span>
            <span className="font-bold text-lg text-green-600">${fees.hostPayout.toFixed(2)}</span>
          </div>
        </div>

        {/* Platform Revenue */}
        <div className="bg-primary-50 -mx-5 -mb-5 px-5 py-4 rounded-b-xl mt-4">
          <div className="flex justify-between">
            <span className="font-semibold text-primary-900">Platform Revenue</span>
            <span className="font-bold text-lg text-primary-600">
              ${fees.platformRevenue.toFixed(2)}
            </span>
          </div>
          <p className="text-xs text-primary-700 mt-1">
            {((fees.platformRevenue / bookingAmount) * 100).toFixed(1)}% of booking amount
          </p>
        </div>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-3 gap-3 mt-4">
        <div className="bg-blue-50 rounded-lg p-3 text-center">
          <p className="text-xs text-blue-600 mb-1">Guest Pays</p>
          <p className="font-semibold text-blue-900">
            100%
          </p>
        </div>
        <div className="bg-green-50 rounded-lg p-3 text-center">
          <p className="text-xs text-green-600 mb-1">Host Keeps</p>
          <p className="font-semibold text-green-900">
            {((fees.hostPayout / bookingAmount) * 100).toFixed(1)}%
          </p>
        </div>
        <div className="bg-primary-50 rounded-lg p-3 text-center">
          <p className="text-xs text-primary-600 mb-1">Platform Commission (10%)</p>
          <p className="font-semibold text-primary-900">
            {((fees.platformRevenue / bookingAmount) * 100).toFixed(1)}%
          </p>
        </div>
      </div>
    </div>
  );
};

export default FeeCalculatorPreview;
